/**
 * Export a conversation.
 *
 * The point is not archival, it is handing the reasoning to someone else: the
 * assistant's answer to "detection or classification for our line scanner?" is
 * the justification a team needs in a ticket or a design doc, and until now it
 * only existed inside a browser tab that a refresh would clear.
 *
 * Markdown is the format because that is what the model already writes and what
 * every issue tracker and wiki renders.
 */

const stamp = (value) => {
  const date = new Date(value || Date.now());
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
};

/** Filesystem-safe slug from a thread title. */
const slugify = (title) =>
  (title || 'conversation')
    .toLowerCase()
    .replace(/[^a-z0-9가-힣]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48) || 'conversation';

/** Render one thread as a markdown document. */
export const conversationToMarkdown = (thread, { userName = 'You' } = {}) => {
  if (!thread) return '';
  const lines = [
    `# ${thread.title || 'LabelFlow assistant conversation'}`,
    '',
    `_Exported ${stamp(Date.now())} from LabelFlow AI Assistant_`,
    '',
    '---',
    '',
  ];

  (thread.messages || []).forEach((message) => {
    const who = message.role === 'user' ? userName : 'LabelFlow Assistant';
    lines.push(`### ${who}`);
    if (message.createdAt) lines.push(`_${stamp(message.createdAt)}_`);
    lines.push('');
    lines.push((message.content || '').trim() || '_(no content)_');
    lines.push('');
  });

  return lines.join('\n');
};

/**
 * Save the markdown as a file.
 *
 * Built with a Blob and a transient anchor rather than a library so the export
 * adds no dependency, and revoked immediately after so the object URL does not
 * pin the blob in memory for the life of the tab.
 */
export const downloadConversation = (thread, options = {}) => {
  const markdown = conversationToMarkdown(thread, options);
  if (!markdown) return false;

  try {
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `labelflow-${slugify(thread.title)}.md`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.warn('Assistant: export failed', error);
    return false;
  }
};

/** Copy the whole conversation as markdown. Returns success. */
export const copyConversation = async (thread, options = {}) => {
  const markdown = conversationToMarkdown(thread, options);
  if (!markdown) return false;
  try {
    await navigator.clipboard.writeText(markdown);
    return true;
  } catch (error) {
    // Clipboard access needs a secure context (https or localhost).
    console.warn('Assistant: copy failed', error);
    return false;
  }
};
